/* ============================================================
   SAM-307 / SAM-306 / SAM-426 — Hik-Connect door PIN state on an invitation.

   The backend (sam-lambda-functions, functions/invitation + functions/hikConnect)
   adds these optional fields to every invitation / business_access JSON:
     hik_pin_status      'pending' | 'ok' | 'failed' | 'skipped'
     hik_pin_error       last error message, or null
     hik_pin_attempts    how many times generation was tried
     hik_pin_updated_at  ISO-8601
     hik_open_count      uses the PIN allows (settings.hikConnect.defaultOpenCount)
     hik_use_count       uses consumed so far (SAM-308 usage log)
     hik_first_used_at   ISO-8601 | null
     hik_valid_from / hik_valid_until   effective door-PIN window, ISO-8601 with offset
   Every field is optional: a row without them renders exactly as before.
   ============================================================ */

import { TIME_ZONE } from '../../i18n/format';

export type HikPinStatus = 'pending' | 'ok' | 'failed' | 'skipped';

export const HIK_PIN_STATUSES: readonly HikPinStatus[] = ['pending', 'ok', 'failed', 'skipped'];

/** Statuses an operator has to look at: the PIN does not exist (yet). */
export const HIK_PIN_ATTENTION_STATUSES: readonly HikPinStatus[] = ['pending', 'failed'];

/** How far ahead the "door PIN not OK" view looks for check-ins. */
export const DOOR_PIN_ATTENTION_WINDOW_HOURS = 48;

export interface HikPinFields {
  hik_pin?: string | null;
  hik_pin_status?: string | null;
  hik_pin_error?: string | null;
  hik_pin_attempts?: number | null;
  hik_pin_updated_at?: string | null;
  hik_open_count?: number | null;
  hik_use_count?: number | null;
  hik_first_used_at?: string | null;
  hik_valid_from?: string | null;
  hik_valid_until?: string | null;
}

export interface HikPinInfo {
  pin: string | null;
  /** null when the backend did not send a (known) status. */
  status: HikPinStatus | null;
  error: string | null;
  attempts: number | null;
  openCount: number | null;
  useCount: number | null;
  validUntil: string | null;
}

const toInt = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

const toStr = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
};

export function normalizeHikPinStatus(v: unknown): HikPinStatus | null {
  const s = String(v ?? '').trim().toLowerCase();
  return (HIK_PIN_STATUSES as readonly string[]).includes(s) ? (s as HikPinStatus) : null;
}

export function readHikPin(row: HikPinFields | null | undefined): HikPinInfo {
  return {
    pin: toStr(row?.hik_pin),
    status: normalizeHikPinStatus(row?.hik_pin_status),
    error: toStr(row?.hik_pin_error),
    attempts: toInt(row?.hik_pin_attempts),
    openCount: toInt(row?.hik_open_count),
    useCount: toInt(row?.hik_use_count),
    validUntil: toStr(row?.hik_valid_until),
  };
}

export function isHikPinAttention(status: HikPinStatus | null): boolean {
  return status !== null && HIK_PIN_ATTENTION_STATUSES.includes(status);
}

/**
 * Whether the row belongs in the "door PIN not OK" view: status pending or failed, the stay
 * has not ended (end_date missing, unparseable, or >= now) and it starts no later than
 * now + windowHours. A stay already in progress counts: its guest may be at the door right now.
 * A row without a parseable start_date is left out.
 */
export function isDoorPinAttention(
  row: (HikPinFields & { start_date?: string | null; end_date?: string | null }) | null | undefined,
  now: Date = new Date(),
  windowHours: number = DOOR_PIN_ATTENTION_WINDOW_HOURS,
): boolean {
  if (!isHikPinAttention(normalizeHikPinStatus(row?.hik_pin_status))) return false;
  const start = row?.start_date ? new Date(row.start_date).getTime() : NaN;
  if (Number.isNaN(start)) return false;
  const nowMs = now.getTime();
  if (start > nowMs + windowHours * 3600 * 1000) return false;
  const end = row?.end_date ? new Date(row.end_date).getTime() : NaN;
  return Number.isNaN(end) || end >= nowMs;
}

/** Whether the invitation detail shows the door PIN block. requireStatus: only when the backend
 * sent a status (used right after create, so a pre-SAM-307 response looks as before). */
export function shouldShowHikPinPanel(info: HikPinInfo, requireStatus: boolean): boolean {
  if (info.status === 'skipped') return !requireStatus && !!info.pin;
  if (requireStatus) return info.status !== null;
  return !!info.pin || info.status !== null || info.validUntil !== null;
}

/** "uses: N of M" — only when the backend sent both counts. */
export function formatHikUses(info: Pick<HikPinInfo, 'useCount' | 'openCount'>): string | null {
  if (info.useCount === null || info.openCount === null) return null;
  return `${info.useCount} of ${info.openCount}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// A value that carries its own offset ("-04:00", "+00:00", "Z") is an instant.
const HAS_OFFSET = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

const wallTime = (y: number, mo: number, d: number, h: number, mi: number): string => {
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${MONTHS[mo - 1]} ${d}, ${y}, ${h12}:${String(mi).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/**
 * Date and time for an end-of-access instant, in Dominican time (America/Santo_Domingo).
 * A value that carries an offset or "Z" (hik_valid_until, e.g. "2026-10-01T18:00:00+00:00") is an
 * instant: it is shown as the clock reads in the Dominican Republic, with "(RD)" appended, whatever
 * zone the API serialised it in or the operator's browser is in (SAM-973; it used to read UTC).
 * A value without an offset is already a wall time and keeps its numbers.
 */
export function formatAccessDateTime(value: string | null | undefined): string | null {
  const s = toStr(value);
  if (!s) return null;
  if (HAS_OFFSET.test(s)) {
    const instant = new Date(s);
    if (Number.isNaN(instant.getTime())) return null;
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone: TIME_ZONE, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
      }).formatToParts(instant).map((x) => [x.type, x.value]),
    );
    return `${wallTime(Number(p.year), Number(p.month), Number(p.day), Number(p.hour), Number(p.minute))} (RD)`;
  }
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return null;
  return wallTime(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes());
}

/** Effective end of the door PIN: hik_valid_until when present, else the invitation end. */
export function effectiveAccessEnd(row: (HikPinFields & { end_date?: string | null }) | null | undefined): string | null {
  return toStr(row?.hik_valid_until) ?? toStr(row?.end_date);
}

/** Toast text after a create whose PIN generation already failed (SAM-307). */
export function hikPinFailureMessage(info: Pick<HikPinInfo, 'error'>): string {
  return info.error
    ? `Invitation created, but the door PIN could not be generated: ${info.error}`
    : 'Invitation created, but the door PIN could not be generated.';
}
